export type UserRole = 'admin' | 'instructor' | 'individual' | 'outside_observer';

export interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: UserRole;
  createdAt: Date;
  updatedAt: Date;
  excludeFromProgress?: boolean;
  deactivated?: boolean;
  deactivatedAt?: Date | null;
  deprecated?: boolean;
  deprecatedAt?: Date | null;
  deprecatedReason?: string;
  /** Identity service person ID (links to central Flowcore identity) */
  identityPersonId?: string | null;
}

export interface ReactivationRequest {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  requestedAt: Date;
  status: 'pending' | 'resolved';
  resolvedAt?: Date | null;
  resolvedBy?: string | null;
}

export interface InstructorActivityEntry {
  id: string;
  instructorId: string;
  instructorName: string;
  studentId: string;
  studentName: string;
  qualificationId: string;
  qualificationName: string;
  itemId: string;
  itemTitle: string;
  category: ItemCategory;
  completedAt: Date;
  passed: boolean;
}

export type ItemCategory = 'discussion' | 'demonstration' | 'ridealong';

export interface JQRItem {
  id: string;
  category: ItemCategory;
  title: string;
  description: string;
  hasRubric: boolean;
  order?: number; // display order within category group
}

export interface Qualification {
  id: string;
  name: string;
  shortName: string;
  description: string;
  prerequisites: string[];
  optional: boolean;
  order: number;
  items: JQRItem[];
  version?: number;
  createdAt?: Date;
  updatedAt?: Date;
  updatedBy?: string;
  deletedAt?: Date | null;
  deletedBy?: string | null;
}

export interface RubricGrades {
  prepare: number | null;
  greet: number | null;
  explore: number | null;
  present: number | null;
  execute: number | null;
  wrapup: number | null;
}

export interface Progress {
  qualificationId: string;
  itemId: string;
  category: ItemCategory;
  completed: boolean;
  completedAt: Date | null;
  completedBy: string | null;
  completedByName: string | null;
  notes: string;
  grade: string | null;
  rubric: RubricGrades | null;
  rubricNotes: Record<string, string> | null;
}

// Library document types
export type LibraryItemType = 'article' | 'pdf' | 'weblink';

/** Base fields shared by all library items */
interface LibraryItemBase {
  id: string;
  type: LibraryItemType;
  title: string;
  slug: string;
  description: string;
  version: number;
  changeSummary: string;
  linkedQualIds: string[]; // qualification IDs, e.g. 'baq', 'electrical'
  skillTreeIds: string[]; // skill tree IDs, e.g. 'water_well'
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
  updatedBy: string;
  updatedByName: string;
  deletedAt: Date | null;
  deletedBy: string | null;
  /** Training manual section id (see data/trainingManual.ts). Unset for regular Library items. */
  manualSection?: string | null;
  /** Week number within the manual section */
  manualWeek?: number | null;
}

export interface LibraryArticle extends LibraryItemBase {
  type: 'article';
  body: string;
}

export interface LibraryPDF extends LibraryItemBase {
  type: 'pdf';
  fileUrl: string; // Firebase Storage URL or external URL
}

export interface LibraryWeblink extends LibraryItemBase {
  type: 'weblink';
  url: string;
  screenshotUrl: string;
}

export type LibraryItem = LibraryArticle | LibraryPDF | LibraryWeblink;

/** @deprecated Use LibraryItem instead */
export type Article = LibraryArticle;

export interface Comment {
  id: string;
  libraryItemId: string;
  userId: string;
  userName: string;
  body: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
  deletedBy?: string | null;
}

// ── Skill Trees & Enrollments ──

export interface SkillTreeDef {
  id: string;
  name: string;
  shortName: string;
  description: string;
  qualificationIds: string[]; // ordered list of qual IDs in this tree
  defaultDeadlines: Record<string, number | null>; // qualId → days from start (null = no deadline)
  version?: number;
  createdAt?: Date;
  updatedAt?: Date;
  updatedBy?: string;
  deletedAt?: Date | null;
  deletedBy?: string | null;
}

/** @deprecated Use SkillTreeDef.defaultDeadlines instead */
export type SkillTreeDefaults = Record<string, number | null>;

export interface Enrollment {
  id: string;
  userId: string;
  skillTreeId: string;
  startDate: Date;
  deadlineOverrides: Record<string, number>; // qualId → days from start (only overridden quals)
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  deletedAt: Date | null;
  deletedBy: string | null;
}

export type TimelineStatus = 'complete' | 'on-track' | 'at-risk' | 'behind' | 'overdue' | 'no-deadline';

export interface Attestation {
  id: string;
  compoundId: string;
  userId: string;
  userName: string;
  userEmail: string;
  articleId: string;
  articleTitle: string;
  articleVersion: number;
  lastAttestedVersion: number | null;
  linkedQualIds: string[];
  changeSummaries: string[];
  attested: boolean;
  attestedAt: Date | null;
  createdAt: Date;
  deletedAt?: Date | null;
  deletedBy?: string | null;
}

// ── Question Bank & Testing ──

export type QuestionTopic =
  | 'tceq-regulations'
  | 'tdlr-licensing'
  | 'groundwater-districts'
  | 'well-design'
  | 'well-construction'
  | 'electrical-safety'
  | 'pump-systems'
  | 'water-quality'
  | 'water-treatment'
  | 'safety-osha'
  | 'hoist-operations'
  | 'calculations'
  | 'documentation'
  | 'field-procedures'
  | 'diagnostics';

export type QuestionDifficulty = 'basic' | 'intermediate' | 'advanced';

export interface QuestionChoice {
  id: string;
  text: string;
}

export interface Question {
  id: string;
  topic: QuestionTopic;
  difficulty: QuestionDifficulty;
  question: string;
  choices: QuestionChoice[];
  correctAnswer: string;
  explanation: string;
  isMath: boolean;
  linkedQualIds: string[];
  deletedAt?: Date | null;
  deletedBy?: string | null;
}

export const QUESTION_TOPICS: { id: QuestionTopic; label: string }[] = [
  { id: 'tceq-regulations', label: 'TCEQ Regulations' },
  { id: 'tdlr-licensing', label: 'TDLR Licensing' },
  { id: 'groundwater-districts', label: 'Groundwater Districts' },
  { id: 'well-design', label: 'Well Design' },
  { id: 'well-construction', label: 'Well Construction' },
  { id: 'electrical-safety', label: 'Electrical Safety' },
  { id: 'pump-systems', label: 'Pump Systems' },
  { id: 'water-quality', label: 'Water Quality' },
  { id: 'water-treatment', label: 'Water Treatment' },
  { id: 'safety-osha', label: 'Safety & OSHA' },
  { id: 'hoist-operations', label: 'Hoist Operations' },
  { id: 'calculations', label: 'Calculations' },
  { id: 'documentation', label: 'Documentation' },
  { id: 'field-procedures', label: 'Field Procedures' },
  { id: 'diagnostics', label: 'Diagnostics' },
];

/** Per-user flashcard performance for a single question */
export interface FlashcardRecord {
  questionId: string;
  correct: number;
  incorrect: number;
  lastSeen: Date;
  streak: number; // consecutive correct
}

/** A saved test definition */
export interface TestDefinition {
  id: string;
  title: string;
  description: string;
  questionIds: string[];
  createdAt: Date;
  createdBy: string;
  createdByName: string;
  timeLimit: number | null; // minutes, null = no limit
  deletedAt?: Date | null;
  deletedBy?: string | null;
}

/** A single test attempt by a user */
export interface TestAttempt {
  id: string;
  testId: string;
  testTitle: string;
  userId: string;
  userName: string;
  answers: Record<string, string>; // questionId → selected answer
  score: number; // percentage 0-100
  correctCount: number;
  totalCount: number;
  startedAt: Date;
  completedAt: Date;
}
